import type {
  PointsBand,
  PointsDefinition,
  PointsMultiplier,
  PointsScope,
  PointsTableRow,
} from './definition';
// `.ts` because this module is run by Node under `--experimental-strip-types`, whose
// resolver does no extensionless lookup — see `packages/ui/tsconfig.json`.
import { parseExpression } from './expression.ts';

/**
 * The client-side reading of what the server will accept.
 *
 * <b>This is feedback, never a control.</b> `PointsSystemDefinitionValidator` on the server is the
 * thing that actually decides, and it runs on every write and again at publish. What this buys is
 * the §9.3 requirement — validate as you type — so an operator sees an overlapping row the moment
 * they make one rather than after a save round trip.
 *
 * The rules are deliberately the same ones, in the same order, with the same severities: schema
 * shape first, then the handful of things a schema cannot state (ranges overlapping, the
 * open-ended row being last). A gap is a **warning** on both sides, because a place matching no
 * row scores 0 and a metric matching no band yields 1.0 — defined answers, just rarely the
 * intended ones. The wording differs on purpose: the server writes for a log, this writes for
 * somebody running a card room.
 *
 * The formula branch is the one rule that is not restated here but *shared*: it runs the client
 * mirror of the server's parser (`expression.ts`), so the grammar cannot drift between the two the
 * way a second reading of it would.
 *
 * **Two validators, because there are two documents (§3.2).** `validateDefinition` checks how a
 * finish becomes points; `validateScope` checks which events get scored, and mirrors the server's
 * separate `PointsScopeValidator`. They were one function while scope lived inside a definition,
 * and splitting them is what makes a definition carrying a `scope` key impossible to write here
 * rather than merely discouraged.
 */

export type IssueSeverity = 'error' | 'warning';

/**
 * Which part of the screen an issue belongs to, so a section can show its own.
 *
 * `scope` is the odd one out and stays in the list: since §3.2 moved scope off the definition it
 * belongs to no section of the *builder* at all — it comes from `validateScope` and is rendered by
 * the preview pane. Kept in the same union so both validators produce one shape and `IssueList`
 * renders either without knowing which it has.
 */
export type IssueSection =
  | 'identity'
  | 'scope'
  | 'scoring'
  | 'fieldMultiplier'
  | 'buyInMultiplier'
  | 'participation'
  | 'aggregation'
  | 'display';

export interface DefinitionIssue {
  section: IssueSection;
  severity: IssueSeverity;
  message: string;
  /** Set when the issue is about one row or band, so the editor can mark that line. */
  rowIndex?: number;
}

export interface DefinitionValidation {
  issues: DefinitionIssue[];
  errors: DefinitionIssue[];
  warnings: DefinitionIssue[];
  /** Whether the definition may be saved. Warnings never block. */
  isValid: boolean;
}

export function validateDefinition(definition: PointsDefinition): DefinitionValidation {
  const issues: DefinitionIssue[] = [];

  checkIdentity(definition, issues);
  checkScoring(definition, issues);
  checkMultiplier(definition.fieldMultiplier, 'fieldMultiplier', 'field size', issues);
  checkMultiplier(definition.buyInMultiplier, 'buyInMultiplier', 'buy-in', issues);
  checkParticipation(definition, issues);
  checkAggregation(definition, issues);
  checkDisplay(definition, issues);

  const errors = issues.filter((i) => i.severity === 'error');
  return {
    issues,
    errors,
    warnings: issues.filter((i) => i.severity === 'warning'),
    isValid: errors.length === 0,
  };
}

/** Every issue for one section, optionally narrowed to a single row. */
export function issuesFor(
  validation: DefinitionValidation,
  section: IssueSection,
  rowIndex?: number,
): DefinitionIssue[] {
  return validation.issues.filter(
    (i) => i.section === section && (rowIndex === undefined || i.rowIndex === rowIndex),
  );
}

export function hasError(validation: DefinitionValidation, section: IssueSection): boolean {
  return validation.errors.some((i) => i.section === section);
}

// ---------------------------------------------------------------- sections

function checkIdentity(definition: PointsDefinition, issues: DefinitionIssue[]): void {
  if (definition.name.trim().length === 0) {
    push(issues, 'identity', 'error', 'Give the system a name.');
  } else if (definition.name.length > 200) {
    push(issues, 'identity', 'error', 'The name is longer than 200 characters.');
  }

  if ((definition.description?.length ?? 0) > 1000) {
    push(issues, 'identity', 'error', 'The description is longer than 1,000 characters.');
  }
}

/**
 * A scope, on its own — §4.2's document, which is no longer part of a definition (§3.2).
 *
 * The rules are unchanged and so is the reason for them: every one of these failures resolves to
 * *no events*, which reads exactly like a filter that legitimately matched none. Mirrors the
 * server's `PointsScopeValidator`, and like `validateDefinition` it is feedback rather than the
 * control.
 *
 * Returned in the same shape so a caller can render it with the same `IssueList`; every issue
 * carries the `scope` section, since a scope is one section by construction.
 */
export function validateScope(scope: PointsScope): DefinitionValidation {
  const issues: DefinitionIssue[] = [];

  const start = realDate(scope.startDate);
  const end = realDate(scope.endDate);

  /*
   * A NULL BOUND IS LEGAL AND IS NOT AN OMISSION (ratings §4.2).
   *
   * This used to read "Choose the first day to score" against a missing bound, on the reasoning
   * that a client stricter than the server is the safe direction. That expired: §5.2's reference
   * rating board is seeded with both bounds null, so the stricter rule now refuses the platform's
   * own preset — and the server has accepted an open bound since Phase 3b, where the schema and
   * `PointsScope.Parse` were relaxed together precisely so no layer could disagree about it.
   *
   * What is still refused is a bound that is present and not a date. That is a different failure
   * and always was: null is "no edge", `2026-02-30` is a typo.
   */
  if (scope.startDate !== null && !start) {
    push(issues, 'scope', 'error', `“${scope.startDate}” isn’t a real date.`);
  }

  if (scope.endDate !== null && !end) {
    push(issues, 'scope', 'error', `“${scope.endDate}” isn’t a real date.`);
  }

  if (start && end && end < start) {
    push(issues, 'scope', 'error', 'The last day is before the first, so this covers no days at all.');
  }

  // #150: a tag with whitespace at either end matches no tournament's tag, since tags are stored
  // trimmed, so a scope requiring one scores nothing. The server refuses it (PointsScopeValidator,
  // and the schema's pattern); without this the save fails with the form reporting no problems.
  for (const tag of [...scope.includeTags, ...scope.excludeTags]) {
    const trimmed = tag.trim();
    if (trimmed !== tag) {
      push(
        issues,
        'scope',
        'error',
        trimmed === ''
          ? `“${tag}” is only spaces, so it is no tag at all.`
          : `“${tag}” has a space at its start or end, so it matches no tournament’s tag. Use “${trimmed}”.`,
      );
    }
  }

  // A tag required and excluded at once matches nothing by construction — and an empty board
  // reads exactly like a filter that legitimately found none.
  const excluded = new Set(scope.excludeTags.map((t) => t.toLowerCase()));
  for (const tag of scope.includeTags) {
    if (excluded.has(tag.toLowerCase())) {
      push(
        issues,
        'scope',
        'error',
        `“${tag}” is both required and excluded, so no event can match it.`,
      );
    }
  }

  if (!Number.isInteger(scope.minFieldSize) || scope.minFieldSize < 0) {
    push(issues, 'scope', 'error', 'The smallest field must be a whole number of entries, or 0.');
  }

  const errors = issues.filter((i) => i.severity === 'error');
  return {
    issues,
    errors,
    warnings: issues.filter((i) => i.severity === 'warning'),
    isValid: errors.length === 0,
  };
}

function checkScoring(definition: PointsDefinition, issues: DefinitionIssue[]): void {
  if (definition.scoring.type === 'formula') {
    // §9.4: never send an invalid expression to the server. The grammar is checked here rather
    // than only its length, because the server parses on write — so anything this misses is a
    // save that fails with the operator looking at a form reporting no problems.
    const parsed = parseExpression(definition.scoring.expression);
    if (!parsed.ok) push(issues, 'scoring', 'error', parsed.error.message);
    return;
  }

  const rows = definition.scoring.rows;
  if (rows.length === 0) {
    push(issues, 'scoring', 'error', 'Add at least one row saying what a place is worth.');
    return;
  }

  const ranges: Range[] = [];

  rows.forEach((row, index) => {
    if (!checkRowNumbers(row, index, issues)) return;

    const from = row.from as number;

    if (row.to !== null && row.to < from) {
      push(
        issues,
        'scoring',
        'error',
        `The last place (${row.to}) is above the first (${from}).`,
        index,
      );
      return;
    }

    // "Runs to the last paid place" swallows everything after it, so on any row but the last it
    // makes every row below unreachable — a table that looks like it pays 28th to 40th and
    // doesn't.
    if (row.to === null && index !== rows.length - 1) {
      push(
        issues,
        'scoring',
        'error',
        'Only the bottom row can run to the last paid place — nothing below it could ever be reached.',
        index,
      );
      return;
    }

    ranges.push({ from, to: row.to, index });
  });

  reportOverlaps(ranges, 'scoring', 'Place', issues);
  reportGaps(ranges, 'scoring', 'Places', 'score nothing', issues);
}

function checkRowNumbers(row: PointsTableRow, index: number, issues: DefinitionIssue[]): boolean {
  let ok = true;

  if (row.from === null) {
    push(issues, 'scoring', 'error', 'Enter the first place this row covers.', index);
    ok = false;
  } else if (!Number.isInteger(row.from) || row.from < 1) {
    push(issues, 'scoring', 'error', 'A place must be a whole number, 1 or higher.', index);
    ok = false;
  }

  if (row.to !== null && (!Number.isInteger(row.to) || row.to < 1)) {
    push(issues, 'scoring', 'error', 'A place must be a whole number, 1 or higher.', index);
    ok = false;
  }

  if (row.points === null) {
    push(issues, 'scoring', 'error', 'Enter the points this row awards.', index);
    ok = false;
  } else if (row.points < 0) {
    push(issues, 'scoring', 'error', 'Points can’t be negative.', index);
    ok = false;
  }

  return ok;
}

function checkMultiplier(
  multiplier: PointsMultiplier<string> | null,
  section: 'fieldMultiplier' | 'buyInMultiplier',
  subject: string,
  issues: DefinitionIssue[],
): void {
  if (!multiplier) return;

  if (
    multiplier.floor !== null &&
    multiplier.cap !== null &&
    multiplier.floor > multiplier.cap
  ) {
    push(
      issues,
      section,
      'error',
      `The smallest counted ${subject} (${multiplier.floor}) is above the largest (${multiplier.cap}), so every event would land outside its own range.`,
    );
  }

  if (multiplier.bands.length === 0) {
    push(issues, section, 'error', 'Add at least one band, or switch this weighting off.');
    return;
  }

  const ranges: Range[] = [];

  multiplier.bands.forEach((band, index) => {
    if (!checkBandNumbers(band, index, section, subject, issues)) return;

    const from = band.from as number;

    if (band.to !== null && band.to < from) {
      push(issues, section, 'error', `The top of the band (${band.to}) is below the bottom (${from}).`, index);
      return;
    }

    if (band.to === null && index !== multiplier.bands.length - 1) {
      push(
        issues,
        section,
        'error',
        'Only the bottom band can be open-ended — nothing below it could ever be reached.',
        index,
      );
      return;
    }

    ranges.push({ from, to: band.to, index });
  });

  reportOverlaps(ranges, section, capitalise(subject), issues);
  // A gap is not a failure: an event matching no band is simply not weighted. It is still almost
  // never what the author meant — bands running to 800 and resuming at 900 leave everything
  // between them at 1.0 with nothing said.
  reportGaps(ranges, section, `${capitalise(subject)}s`, 'are not weighted', issues);
}

function checkBandNumbers(
  band: PointsBand,
  index: number,
  section: 'fieldMultiplier' | 'buyInMultiplier',
  subject: string,
  issues: DefinitionIssue[],
): boolean {
  let ok = true;

  if (band.from === null) {
    push(issues, section, 'error', `Enter the smallest ${subject} this band covers.`, index);
    ok = false;
  }

  if (band.value === null) {
    push(issues, section, 'error', 'Enter the multiplier for this band.', index);
    ok = false;
  } else if (band.value < 0) {
    push(issues, section, 'error', 'A multiplier can’t be negative.', index);
    ok = false;
  }

  return ok;
}

function checkParticipation(definition: PointsDefinition, issues: DefinitionIssue[]): void {
  if (definition.participation.mode === 'none') return;

  if (definition.participation.pointsPerEntry === null) {
    push(issues, 'participation', 'error', 'Enter what one entry is worth.');
  } else if (definition.participation.pointsPerEntry < 0) {
    push(issues, 'participation', 'error', 'Points per entry can’t be negative.');
  }
}

function checkAggregation(definition: PointsDefinition, issues: DefinitionIssue[]): void {
  if (definition.aggregation.mode !== 'bestN') return;

  if (!Number.isInteger(definition.aggregation.n) || definition.aggregation.n < 1) {
    push(issues, 'aggregation', 'error', 'The number of events to count must be 1 or more.');
  }
}

function checkDisplay(definition: PointsDefinition, issues: DefinitionIssue[]): void {
  const places = definition.display.decimalPlaces;
  if (!Number.isInteger(places) || places < 0 || places > 6) {
    push(issues, 'display', 'error', 'Decimal places must be a whole number between 0 and 6.');
  }
}

// ---------------------------------------------------------------- ranges

interface Range {
  from: number;
  to: number | null;
  index: number;
}

/**
 * Two ranges covering the same value. First match wins, so an overlap crashes nothing — it
 * quietly makes part of a row unreachable, which is the kind of wrong that only ever shows up in
 * somebody's points total.
 */
function reportOverlaps(
  ranges: Range[],
  section: IssueSection,
  unit: string,
  issues: DefinitionIssue[],
): void {
  const ordered = [...ranges].sort((a, b) => a.from - b.from || a.index - b.index);

  for (let i = 1; i < ordered.length; i++) {
    const previous = ordered[i - 1]!;
    const current = ordered[i]!;
    const previousTo = previous.to ?? Number.POSITIVE_INFINITY;

    if (current.from <= previousTo) {
      issues.push({
        section,
        severity: 'error',
        message: `${unit} ${format(current.from)} is covered twice — the row above wins, so part of this one never applies.`,
        rowIndex: current.index,
      });
    }
  }
}

/**
 * Something whole falling between two ranges. 800 then 801 is contiguous; 800 then 900 is not,
 * and everything between them silently takes the default. Every metric here is a whole number —
 * places, entries, unique players, cashers, and buy-in and prize pool, which are integer dollars.
 */
function reportGaps(
  ranges: Range[],
  section: IssueSection,
  subject: string,
  consequence: string,
  issues: DefinitionIssue[],
): void {
  const ordered = [...ranges].sort((a, b) => a.from - b.from || a.index - b.index);

  for (let i = 1; i < ordered.length; i++) {
    const end = ordered[i - 1]!.to;
    if (end === null) return; // open-ended: nothing after it can be a gap

    const start = ordered[i]!.from;
    if (start <= end + 1) continue;

    issues.push({
      section,
      severity: 'warning',
      message: `${subject} between ${format(end)} and ${format(start)} match no entry, so they ${consequence}.`,
      rowIndex: ordered[i]!.index,
    });
  }
}

// ---------------------------------------------------------------- helpers

function push(
  issues: DefinitionIssue[],
  section: IssueSection,
  severity: IssueSeverity,
  message: string,
  rowIndex?: number,
): void {
  issues.push(rowIndex === undefined ? { section, severity, message } : { section, severity, message, rowIndex });
}

/**
 * `yyyy-mm-dd`, and a day that exists. The spelling is checked by the schema; only a calendar
 * knows that 2026-02-30 is not a date — and a scope pinned to one resolves to no events at all,
 * which an operator reads as a broken tag filter and hunts for in the wrong place.
 */
export function realDate(value: string | null): string | null {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? value
    : null;
}

function format(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10000) / 10000);
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
